// Live's Multi-Shot (#230): a video made of up to `max_shots` shots, each with its own prompt and
// length, whose lengths share one budget (`shot_seconds.max`, live's 15 seconds). The rules are
// live's `useShotsBudget`: a shot is at least `shot_seconds.min` long, a shot can be added while
// the budget left holds one more, and each shot may take what the others leave. One rule is ours
// (Arturas, 2026-10-28, spec web/shot-defaults): the shots together last at least the model's
// shortest video, which the start refuses below ("The shots last 2 s; Kling 4.0 makes videos of
// at least 3 s") and live's form lets through.
import type {
  CatalogImageModel,
  CatalogVideoModel,
  GenerationType,
  MultiShotCaps,
} from "../../../api/generated/model";

/** One shot as the composer holds it. */
export interface ShotDraft {
  prompt: string;
  durationSeconds: number;
}

/** The type's multi-shot caps, or null where the model makes no multi-shot video. */
export function multiShotOf(
  model: CatalogImageModel | CatalogVideoModel,
  type: GenerationType,
): MultiShotCaps | null {
  return model.medium === "video" ? (model.modes[type]?.multi_shot ?? null) : null;
}

/**
 * The shortest video the type makes, its block's lengths else the row's (the start's
 * `row.options`), or null where it takes no length: the shots together never last less.
 */
export function shortestOf(
  model: CatalogImageModel | CatalogVideoModel,
  type: GenerationType,
): number | null {
  if (model.medium !== "video") return null;
  const lengths = (model.modes[type]?.durations ?? model.durations)?.values ?? [];
  return lengths.length === 0 ? null : Math.min(...lengths);
}

/** Live's first shot, with no prompt and the shortest length, but as long as the shortest video. */
export function firstShots(
  caps: MultiShotCaps | null,
  shortest: number | null = null,
): ShotDraft[] {
  const least = caps?.shot_seconds.min ?? 1;
  const most = caps?.shot_seconds.max ?? least;
  return [{ prompt: "", durationSeconds: Math.min(Math.max(least, shortest ?? least), most) }];
}

/**
 * The shots trimmed to another model's caps: at most its shots, each length inside its budget,
 * and their last grown where they fall short of the model's shortest video.
 */
export function fitShots(
  shots: readonly ShotDraft[],
  caps: MultiShotCaps,
  shortest: number | null = null,
): ShotDraft[] {
  const { min, max } = caps.shot_seconds;
  const kept = shots.slice(0, caps.max_shots);
  let left = max;
  const fitted = kept.map((shot, at) => {
    // Room for the shots after this one, at their shortest.
    const room = left - min * (kept.length - at - 1);
    const durationSeconds = Math.max(min, Math.min(shot.durationSeconds, room));
    left -= durationSeconds;
    return { ...shot, durationSeconds };
  });
  const total = fitted.reduce((sum, shot) => sum + shot.durationSeconds, 0);
  const last = fitted.at(-1);
  if (shortest === null || last === undefined || total >= shortest) return fitted;
  // The last shot makes up what the shots lack, within the budget the others leave it.
  const grown = Math.min(last.durationSeconds + shortest - total, last.durationSeconds + left);
  return [...fitted.slice(0, -1), { ...last, durationSeconds: grown }];
}

export interface ShotBudget {
  /** The shortest a shot may be, a new one's length. */
  min: number;
  /** The shortest the active shot may be: a shot's least, or what the others leave short of
   * the model's shortest video. */
  activeMin: number;
  /** A shot can be added: fewer than the most shots, and the budget left holds one more. */
  canAdd: boolean;
  /** The longest the active shot may be: its own length and what the others leave. */
  activeMax: number;
}

/** Live's budget for the shots, with `active` the shot being edited. */
export function shotBudget(
  shots: readonly ShotDraft[],
  caps: MultiShotCaps,
  active: number,
  shortest: number | null = null,
): ShotBudget {
  const { min, max } = caps.shot_seconds;
  const used = shots.reduce((sum, shot) => sum + shot.durationSeconds, 0);
  const own = shots[active]?.durationSeconds ?? min;
  const activeMax = own + max - used;
  return {
    min,
    activeMin: Math.min(Math.max(min, (shortest ?? 0) - (used - own)), activeMax),
    canAdd: shots.length < caps.max_shots && max - used >= min,
    activeMax,
  };
}
